import React, { useState, useEffect } from 'react';
import { 
  FiZap, FiCheck, FiPlay, FiUser, FiClock, FiPackage,
  FiAlertTriangle, FiTrendingUp
} from 'react-icons/fi';

// Activity types with styling (anonymous - no dispatcher names)
const ACTIVITY_TYPES = {
  CLAIMED: {
    icon: FiZap,
    color: 'text-[#4F46E5] bg-[#E0E7FF] ring-1 ring-[#A5B4FC]/50',
    label: 'Ticket claimed',
  },
  STARTED: {
    icon: FiPlay,
    color: 'text-[#B45309] bg-[#FEF3C7] ring-1 ring-[#FCD34D]/50',
    label: 'Work started',
  },
  COMPLETED: {
    icon: FiCheck,
    color: 'text-[#047857] bg-[#D1FAE5] ring-1 ring-[#6EE7B7]/50',
    label: 'Ticket completed',
  },
  NEW_P0: {
    icon: FiAlertTriangle,
    color: 'text-[#B91C1C] bg-[#FEE2E2] ring-1 ring-[#FCA5A5]/50 animate-pulse',
    label: 'Critical ticket',
  },
  NEW_TICKET: {
    icon: FiPackage,
    color: 'text-[#0369A1] bg-[#DBEAFE] ring-1 ring-[#93C5FD]/50',
    label: 'New ticket',
  },
};

// Format relative time
function formatRelativeTime(date) {
  const now = new Date();
  const diff = now - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function TeamActivityFeed({ 
  activities = [],
  maxItems = 10,
  showHeader = true,
  className = '',
}) {
  const [displayedActivities, setDisplayedActivities] = useState([]);

  // Update displayed activities with animation trigger
  useEffect(() => {
    setDisplayedActivities(activities.slice(0, maxItems));
  }, [activities, maxItems]);

  if (displayedActivities.length === 0) {
    return (
      <div className={`rounded-2xl bg-white border border-zinc-200 p-6 ${className}`}>
        {showHeader && (
          <div className="flex items-center gap-2 mb-4">
            <FiTrendingUp className="w-5 h-5 text-zinc-400" />
            <h3 className="font-semibold text-zinc-900">Team Activity</h3>
          </div>
        )}
        <div className="text-center py-8">
          <FiClock className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No recent activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl bg-white border border-zinc-200 overflow-hidden ${className}`}>
      {showHeader && (
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiTrendingUp className="w-5 h-5 text-indigo-500" />
            <h3 className="font-semibold text-zinc-900">Team Activity</h3>
          </div>
          <span className="text-xs text-zinc-500">Live</span>
        </div>
      )}
      
      <div className="divide-y divide-zinc-50 max-h-80 overflow-y-auto">
        {displayedActivities.map((activity, idx) => {
          const config = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.NEW_TICKET;
          const Icon = config.icon;
          const isNew = idx === 0 && activity.isNew;
          
          return (
            <div
              key={activity.id || idx}
              className={`
                px-5 py-3 flex items-start gap-3 transition-colors
                hover:bg-zinc-50
                ${isNew ? 'bg-indigo-50/50 animate-slide-in-up' : ''}
              `}
            >
              {/* Icon */}
              <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                <Icon className="w-4 h-4" />
              </div>
              
              {/* Content - Anonymous (no dispatcher name) */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-900">
                  <span className="font-medium">{config.label}</span>
                  {activity.ticketName && (
                    <>
                      <span className="text-zinc-500"> - </span>
                      <span className="text-zinc-700">{activity.ticketName}</span>
                    </>
                  )}
                </p>
                {activity.orderId && (
                  <p className="text-xs text-zinc-500 font-mono mt-0.5">
                    Order #{activity.orderUniqueId}
                  </p>
                )}
              </div>
              
              {/* Time */}
              <span className="text-xs text-zinc-400 flex-shrink-0">
                {formatRelativeTime(activity.timestamp)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100">
        <p className="text-xs text-zinc-500 text-center">
          Showing last {displayedActivities.length} activities
        </p>
      </div>
    </div>
  );
}

// Compact inline version for header
export function ActivityIndicator({ activities = [] }) {
  const recentCount = activities.filter(a => {
    const diff = Date.now() - new Date(a.timestamp);
    return diff < 5 * 60 * 1000; // Last 5 minutes
  }).length;

  if (recentCount === 0) return null;

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50">
      <span className="w-2 h-2 rounded-full bg-[#10B981] ring-1 ring-[#059669] animate-pulse" />
      <span className="text-xs font-medium">{recentCount} active</span>
    </div>
  );
}

